import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import { APIError, endpointToken, fetchAttendance, setAttendance } from "./api";
import { attendanceFeedback, UNDO_TOAST_DURATION_MS } from "./attendanceFeedback";
import type { ToastState } from "./attendanceFeedback";
import { CheckIcon, CheckboxIcon, RefreshIcon, SearchIcon } from "./icons";
import { attendanceSections, memberInitials } from "./members";
import { loadPending, storePending, upsertPending } from "./pending";
import { markPhotoLoaded, schedulePhotoWarmup } from "./photoCache";
import type { Member, PendingAction, Snapshot } from "./types";
import { useVisualViewport } from "./useVisualViewport";

const token = endpointToken(window.location.pathname, window.location.search);

export default function App() {
  useVisualViewport();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [initialError, setInitialError] = useState<string | null>(null);
  const [busyCount, setBusyCount] = useState(0);
  const [offline, setOffline] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [markedView, setMarkedView] = useState(false);
  const [confirmation, setConfirmation] = useState<Member | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [queued, setQueued] = useState<PendingAction[]>(() => (token ? loadPending(token) : []));
  const [inFlight, setInFlight] = useState<Set<string>>(new Set());
  const latestSync = useRef(0);
  const memberScroll = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const cancelConfirmation = useRef<HTMLButtonElement>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const locallyMarkedIDs = useRef(new Set<string>());

  const syncing = busyCount > 0;
  const pendingIDs = useMemo(() => {
    const ids = new Set(inFlight);
    for (const action of queued) ids.add(action.memberId);
    return ids;
  }, [inFlight, queued]);

  const memberView = useMemo(() => {
    if (!snapshot) {
      return { sections: [], members: [], priorityPhotoIDs: new Set<string>() };
    }
    const sections = attendanceSections(snapshot.members, query, markedView);
    const members = snapshot.members.filter((member) => member.attending === markedView);
    const priorityMembers = sections.flatMap((section) => section.members).slice(0, 3);
    const priorityPhotoIDs = new Set(priorityMembers.map((member) => member.id));
    return { sections, members, priorityPhotoIDs };
  }, [markedView, query, snapshot]);

  const photoURLKey = useMemo(
    () => snapshot?.members.map((member) => member.photo_url).filter(Boolean).join("\n") ?? "",
    [snapshot],
  );

  const applySnapshot = useCallback((next: Snapshot) => {
    const timestamp = Date.parse(next.synced_at) || Date.now();
    if (timestamp >= latestSync.current) {
      latestSync.current = timestamp;
      setSnapshot(next);
    }
    setInitialError(null);
    setOffline(false);
  }, []);

  const showToast = useCallback((next: ToastState | null, milliseconds = 4200) => {
    window.clearTimeout(toastTimer.current);
    setToast(next);
    if (next) toastTimer.current = window.setTimeout(() => setToast(null), milliseconds);
  }, []);

  const startBusy = () => setBusyCount((count) => count + 1);
  const stopBusy = () => setBusyCount((count) => Math.max(0, count - 1));

  const replaceQueue = useCallback((next: PendingAction[]) => {
    setQueued(next);
    if (token) storePending(token, next);
  }, []);

  const load = useCallback(async (announce = false) => {
    if (!token) {
      setInitialError("This attendance link is incomplete.");
      return;
    }
    startBusy();
    try {
      let remainingQueue = loadPending(token);
      for (const action of remainingQueue) {
        const next = await setAttendance(token, action.memberId, action.attending);
        applySnapshot(next);
        remainingQueue = remainingQueue.filter((candidate) => candidate.memberId !== action.memberId);
        replaceQueue(remainingQueue);
      }
      const next = await fetchAttendance(token);
      applySnapshot(next);
      if (announce) {
        showToast({ title: "Up to date" }, 2400);
      }
    } catch (error) {
      setOffline(true);
      if (!snapshot) setInitialError(readableError(error));
      showToast({ title: "Couldn’t reach the list", subtitle: "Showing the last version you pulled" }, 3400);
    } finally {
      stopBusy();
    }
  }, [applySnapshot, replaceQueue, showToast, snapshot]);

  useEffect(() => {
    void load(false);
    return () => window.clearTimeout(toastTimer.current);
    // Initial fetch is deliberately tied only to the endpoint token. There is
    // no polling: other markers surface only after explicit local input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!photoURLKey) return;
    let stopWarmup: () => void = () => undefined;
    const frame = requestAnimationFrame(() => {
      stopWarmup = schedulePhotoWarmup(photoURLKey.split("\n"), () => {
        const scroller = memberScroll.current;
        return scroller ? visiblePhotoURLs(scroller) : [];
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      stopWarmup();
    };
  }, [photoURLKey]);

  useEffect(() => {
    if (!searchOpen) return;
    memberScroll.current?.scrollTo({ top: 0, behavior: "auto" });
    const frame = requestAnimationFrame(() => {
      searchInput.current?.focus({ preventScroll: true });
      memberScroll.current?.scrollTo({ top: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(frame);
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    memberScroll.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [query, searchOpen]);

  useEffect(() => {
    if (!confirmation) return;
    const frame = requestAnimationFrame(() => cancelConfirmation.current?.focus());
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setConfirmation(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [confirmation]);

  const mark = async (member: Member, attending = true) => {
    if (!token || pendingIDs.has(member.id)) return;
    const before = snapshot;
    if (attending) {
      locallyMarkedIDs.current.add(member.id);
    } else {
      locallyMarkedIDs.current.delete(member.id);
    }
    setInFlight((ids) => new Set(ids).add(member.id));
    startBusy();
    try {
      const next = await setAttendance(token, member.id, attending);
      applySnapshot(next);
      const feedback = attendanceFeedback(member, attending, before, next, locallyMarkedIDs.current);
      if (attending) {
        showToast(feedback, UNDO_TOAST_DURATION_MS);
      } else {
        showToast(feedback);
      }
    } catch {
      const action = { memberId: member.id, memberName: member.name, attending };
      const nextQueue = upsertPending(loadPending(token), action);
      replaceQueue(nextQueue);
      setOffline(true);
      showToast({ title: `${member.name} queued`, subtitle: "Tap refresh when the connection returns" });
    } finally {
      setInFlight((ids) => {
        const next = new Set(ids);
        next.delete(member.id);
        return next;
      });
      stopBusy();
    }
  };

  const undo = () => {
    if (!toast?.undo) return;
    const member = toast.undo;
    showToast(null);
    void mark(member, false);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const selectMarkedView = (next: boolean) => {
    if (next === markedView) return;
    setMarkedView(next);
    setQuery("");
    setConfirmation(null);
    memberScroll.current?.scrollTo({ top: 0, behavior: "auto" });
  };

  if (!token) {
    return <MessageScreen title="Link needed" detail="Open the complete link from the welcome-team email." />;
  }

  if (!snapshot && initialError && !syncing) {
    return <MessageScreen title="Couldn’t open this list" detail={initialError} action={() => void load(false)} />;
  }

  if (!snapshot) return <LoadingScreen />;

  const { sections, members, priorityPhotoIDs } = memberView;
  const noResults = sections.length === 0 && members.length > 0;
  const emptyView = members.length === 0;
  const allDone = !markedView && emptyView;

  return (
    <main className="app-shell">
      <div className="member-scroll" id="member-scroll" ref={memberScroll}>
        <div className="member-content">
          {sections.map((section) => (
            <section className="letter-section" id={sectionID(section.letter)} key={section.letter}>
              <div className="letter-heading" aria-hidden="true">
                <span>{section.letter}</span>
                <i />
              </div>
              <div className="member-grid">
                {section.members.map((member) => (
                  <MemberCard
                    member={member}
                    pending={pendingIDs.has(member.id)}
                    priority={priorityPhotoIDs.has(member.id)}
                    attending={markedView}
                    onMark={() => {
                      if (markedView) {
                        closeSearch();
                        setConfirmation(member);
                      } else {
                        void mark(member);
                      }
                    }}
                    key={member.id}
                  />
                ))}
              </div>
            </section>
          ))}

          {noResults && (
            <div className="empty-state">
              <h1>{markedView ? "No marked match" : "No unmarked match"}</h1>
              <p>
                {markedView
                  ? `Nobody marked as attending matches “${query}”.`
                  : `Nobody left to mark matches “${query}”. They may already be checked in.`}
              </p>
            </div>
          )}

          {markedView && emptyView && (
            <div className="empty-state all-done">
              <div className="done-check muted-check"><CheckboxIcon checked /></div>
              <h1>No one marked yet</h1>
              <p>Choose To mark below to return to marking attendees.</p>
            </div>
          )}

          {allDone && (
            <div className="empty-state all-done">
              <div className="done-check"><CheckIcon /></div>
              <h1>Everyone’s in</h1>
              <p>All {snapshot.total} members are marked attending.</p>
            </div>
          )}
        </div>
      </div>

      {sections.length > 1 && (
        <LetterIndex letters={sections.map((section) => section.letter)} scrollRef={memberScroll} />
      )}

      {offline && (
        <div className="offline-banner" role="status">
          <span>Couldn’t reach the list — marks are queued.</span>
          <button type="button" onClick={() => void load(false)}>Retry</button>
        </div>
      )}

      {toast && (
        <div className="toast" role="status" aria-live="polite">
          <div>
            <strong>{toast.title}</strong>
            {toast.subtitle && <span>{toast.subtitle}</span>}
          </div>
          {toast.undo && <button type="button" onClick={undo}>Undo</button>}
        </div>
      )}

      <footer className="action-bar">
        {searchOpen ? (
          <div className="search-row">
            <label className="search-field">
              <SearchIcon />
              <span className="sr-only">Search {markedView ? "marked" : "unmarked"} attendees</span>
              <input
                ref={searchInput}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search members"
                autoComplete="off"
                enterKeyHint="search"
              />
            </label>
            <button className="done-button" type="button" onClick={closeSearch}>Done</button>
          </div>
        ) : (
          <>
            <div className="meeting-summary">
              <strong>{snapshot.meeting.label}</strong>
              <div className="attendance-toggle" role="group" aria-label="Attendance view">
                <button
                  type="button"
                  onClick={() => selectMarkedView(false)}
                  aria-label={`Show ${snapshot.remaining} people to mark`}
                  aria-pressed={!markedView}
                >
                  To mark <small>{snapshot.remaining}</small>
                </button>
                <button
                  type="button"
                  onClick={() => selectMarkedView(true)}
                  aria-label={`Show ${snapshot.marked} marked attendees`}
                  aria-pressed={markedView}
                >
                  Marked <small>{snapshot.marked}</small>
                </button>
              </div>
            </div>
            <div className="action-buttons">
              <button className="round-button search-button" type="button" onClick={() => setSearchOpen(true)} aria-label={`Search ${markedView ? "marked" : "unmarked"} attendees`}>
                <SearchIcon />
              </button>
              <button className="round-button refresh-button" type="button" onClick={() => void load(true)} aria-label={syncing ? "Refreshing attendance" : "Refresh attendance"} disabled={syncing}>
                <RefreshIcon className={syncing ? "spinning" : ""} />
              </button>
            </div>
          </>
        )}
      </footer>

      {confirmation && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setConfirmation(null);
        }}>
          <section className="confirmation-modal" role="alertdialog" aria-modal="true" aria-labelledby="unmark-title" aria-describedby="unmark-detail">
            <div className="modal-icon" aria-hidden="true"><CheckboxIcon checked /></div>
            <h1 id="unmark-title">Mark {confirmation.name} as not attending?</h1>
            <p id="unmark-detail">
              This sets {confirmation.name} as not attending. To mark someone else instead, tap Keep attending, then choose To mark below.
            </p>
            <div className="modal-actions">
              <button ref={cancelConfirmation} className="modal-cancel" type="button" onClick={() => setConfirmation(null)}>Keep attending</button>
              <button className="modal-confirm" type="button" onClick={() => {
                const member = confirmation;
                setConfirmation(null);
                void mark(member, false);
              }}>Mark not attending</button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

function LetterIndex({ letters, scrollRef }: { letters: string[]; scrollRef: RefObject<HTMLDivElement | null> }) {
  const rail = useRef<HTMLElement>(null);
  const pointerID = useRef<number | null>(null);
  const lastLetter = useRef<string | null>(null);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  const jumpTo = useCallback((letter: string, behavior: ScrollBehavior) => {
    const scroller = scrollRef.current;
    const section = document.getElementById(sectionID(letter));
    if (!scroller || !section) return;
    scroller.scrollTo({ top: Math.max(0, section.offsetTop - 8), behavior });
    lastLetter.current = letter;
    setActiveLetter(letter);
  }, [scrollRef]);

  const scrubAt = useCallback((clientY: number) => {
    const buttons = Array.from(rail.current?.querySelectorAll<HTMLButtonElement>("button[data-letter]") ?? []);
    let closest: HTMLButtonElement | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const button of buttons) {
      const bounds = button.getBoundingClientRect();
      const distance = Math.abs(clientY - (bounds.top + bounds.bottom) / 2);
      if (distance < closestDistance) {
        closest = button;
        closestDistance = distance;
      }
    }
    const letter = closest?.dataset.letter;
    if (letter && letter !== lastLetter.current) jumpTo(letter, "auto");
  }, [jumpTo]);

  const beginScrub = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    pointerID.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    scrubAt(event.clientY);
  };

  const continueScrub = (event: ReactPointerEvent<HTMLElement>) => {
    if (pointerID.current !== event.pointerId) return;
    event.preventDefault();
    scrubAt(event.clientY);
  };

  const endScrub = (event: ReactPointerEvent<HTMLElement>) => {
    if (pointerID.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointerID.current = null;
    lastLetter.current = null;
  };

  return (
    <nav
      className="letter-index"
      aria-label="Scrub through last-name initials"
      ref={rail}
      onPointerDown={beginScrub}
      onPointerMove={continueScrub}
      onPointerUp={endScrub}
      onPointerCancel={endScrub}
      onLostPointerCapture={() => {
        pointerID.current = null;
        lastLetter.current = null;
      }}
    >
      {letters.map((letter) => (
        <button
          type="button"
          key={letter}
          data-letter={letter}
          className={activeLetter === letter ? "active" : undefined}
          onClick={() => jumpTo(letter, "smooth")}
          aria-label={`Names beginning with ${letter}`}
          aria-current={activeLetter === letter ? "true" : undefined}
        >
          {letter}
        </button>
      ))}
    </nav>
  );
}

function sectionID(letter: string): string {
  return `section-${encodeURIComponent(letter)}`;
}

function MemberCard({ member, pending, priority, attending, onMark }: { member: Member; pending: boolean; priority: boolean; attending: boolean; onMark: () => void }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [member.photo_url]);
  return (
    <button
      className="member-card"
      type="button"
      onClick={onMark}
      disabled={pending}
      aria-label={pending ? `${member.name}, attendance change syncing` : attending ? `Mark ${member.name} as not attending` : `Mark ${member.name} attending`}
    >
      <span className="photo-wrap">
        {member.photo_url && !imageFailed ? (
          <img
            src={member.photo_url}
            data-photo-url={member.photo_url}
            alt=""
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            width="160"
            height="160"
            onLoad={() => markPhotoLoaded(member.photo_url)}
            onError={() => {
              markPhotoLoaded(member.photo_url);
              setImageFailed(true);
            }}
          />
        ) : (
          <span className="photo-fallback">{memberInitials(member.name)}</span>
        )}
        {pending && (
          <span className="pending-overlay" aria-hidden="true">
            <span><CheckIcon /></span>
          </span>
        )}
      </span>
      <span className="member-name">{member.name}</span>
    </button>
  );
}

function visiblePhotoURLs(scroller: HTMLElement): string[] {
  const viewport = scroller.getBoundingClientRect();
  return Array.from(scroller.querySelectorAll<HTMLImageElement>("img[data-photo-url]"))
    .filter((image) => {
      const bounds = image.getBoundingClientRect();
      return bounds.bottom > viewport.top && bounds.top < viewport.bottom && bounds.right > viewport.left && bounds.left < viewport.right;
    })
    .map((image) => image.dataset.photoUrl ?? "")
    .filter(Boolean);
}

function LoadingScreen() {
  return (
    <main className="app-shell loading-shell" aria-label="Loading attendance list">
      <div className="skeleton-grid">
        {Array.from({ length: 12 }, (_, index) => <span className="skeleton-card" key={index} />)}
      </div>
      <div className="loading-pill"><RefreshIcon className="spinning" /> Pulling the list</div>
    </main>
  );
}

function MessageScreen({ title, detail, action }: { title: string; detail: string; action?: () => void }) {
  return (
    <main className="app-shell message-screen">
      <div className="message-mark">A</div>
      <h1>{title}</h1>
      <p>{detail}</p>
      {action && <button type="button" onClick={action}>Try again</button>}
    </main>
  );
}

function readableError(error: unknown): string {
  if (error instanceof APIError && error.code === "endpoint_not_found") {
    return "This link is invalid or no longer active. Ask the coordinator for a fresh link.";
  }
  if (error instanceof Error) return error.message;
  return "Check your connection and try again.";
}
